import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchBoardTechnicianAvatarUrls } from '../services/boardTechnicianAvatarService';

/** Renova ticket ~antes do TTL (30 min no backend). Sem poll agressivo. */
const REFRESH_MS = 20 * 60 * 1000;
const IDS_DEBOUNCE_MS = 800;

function idsFingerprint(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return [...new Set(list.map((id) => String(id ?? '').trim()).filter(Boolean))].sort().join('|');
}

function normalizeIds(technicianIds) {
  if (!Array.isArray(technicianIds)) return [];
  return [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))];
}

function urlsFingerprint(map) {
  if (!map || typeof map !== 'object') return '';
  return Object.keys(map)
    .sort()
    .map((k) => `${k}=${map[k] || ''}`)
    .join('|');
}

/**
 * Avatares S3 (proxy opaco) para colunas do Board.
 * 1 POST batch ao mudar IDs + refresh lento (ticket). Pausa com aba oculta.
 *
 * @param {string[]} technicianIds
 * @param {{ enabled?: boolean }} [options]
 * @returns {Map<string, string>}
 */
export function useBoardTechnicianAvatars(technicianIds, options = {}) {
  const enabled = options.enabled !== false;
  const [urlMap, setUrlMap] = useState({});

  const idsKey = useMemo(() => idsFingerprint(normalizeIds(technicianIds)), [technicianIds]);
  const idsRef = useRef([]);
  idsRef.current = normalizeIds(technicianIds);

  useEffect(() => {
    if (!enabled) {
      setUrlMap((prev) => (Object.keys(prev).length ? {} : prev));
      return undefined;
    }

    let cancelled = false;
    let debounceTimer = null;
    let refreshTimer = null;

    const setIfChanged = (next) => {
      const safe = next && typeof next === 'object' ? next : {};
      setUrlMap((prev) => (urlsFingerprint(prev) === urlsFingerprint(safe) ? prev : safe));
    };

    const load = async () => {
      const ids = idsRef.current;
      if (!ids.length) {
        setIfChanged({});
        return;
      }
      try {
        const data = await fetchBoardTechnicianAvatarUrls(ids);
        if (cancelled) return;
        setIfChanged(data.urls || {});
      } catch {
        if (!cancelled) setIfChanged({});
      }
    };

    const scheduleRefresh = () => {
      if (refreshTimer) window.clearInterval(refreshTimer);
      refreshTimer = window.setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        load();
      }, REFRESH_MS);
    };

    const idsChanged = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        load();
        scheduleRefresh();
      }, IDS_DEBOUNCE_MS);
    };

    idsChanged();

    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      if (debounceTimer) window.clearTimeout(debounceTimer);
      if (refreshTimer) window.clearInterval(refreshTimer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, idsKey]);

  return useMemo(() => {
    /** @type {Map<string, string>} */
    const map = new Map();
    Object.entries(urlMap || {}).forEach(([id, url]) => {
      const href = String(url || '').trim();
      if (!href) return;
      const raw = String(id ?? '').trim();
      if (!raw) return;
      map.set(raw, href);
      const digits = raw.replace(/\D/g, '');
      if (digits) {
        map.set(digits, href);
        map.set(digits.padStart(4, '0').slice(-4), href);
      }
    });
    return map;
  }, [urlMap]);
}
